import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Plus, Search, Filter, ArrowLeft } from 'lucide-react';
import ProductCard from '../components/products/ProductCard';

const Products = () => {
  const { collectionId } = useParams();
  const { collections, products, getProductsByCollection } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filterBy, setFilterBy] = useState('all');

  // Debug logging
  console.log('Products page - collectionId from URL:', collectionId, typeof collectionId);
  console.log('Products page - available collections:', collections.length);
  console.log('Products page - available products:', products.length);

  // Get the collection if viewing products for a specific collection
  const collection = collectionId ? 
    collections.find(c => 
      c.id === collectionId || 
      c.id === parseInt(collectionId) || 
      c.id === String(collectionId)
    ) : null;
  
  console.log('Products page - found collection:', collection?.name || 'none');
  
  // Get products based on whether we're viewing all products or collection-specific products
  const baseProducts = collectionId ? getProductsByCollection(collectionId) : products;
  
  console.log('Products page - baseProducts:', baseProducts.length, 'products found');
  
  const filteredProducts = baseProducts
    .filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterBy === 'all' || 
        (filterBy === 'low-stock' && product.stock < 20) ||
        (filterBy === 'high-price' && product.price > 100);
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'stock') return a.stock - b.stock;
      return b.id - a.id; // newest first by default
    });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          {collectionId && (
            <Link 
              to="/collections"
              className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-2"
            >
              <ArrowLeft size={16} className="mr-1" />
              Back to Collections
            </Link>
          )}
          <h1 className="text-2xl font-bold text-gray-900">
            {collection ? `${collection.name} Products` : 'All Products'}
          </h1>
          <p className="text-gray-600 mt-1">
            {collection ? collection.description : 'Manage all your products across collections'}
          </p>
        </div>
        
        <Link
          to={collectionId ? `/create-product/${collectionId}` : '/create-product'}
          className="mt-4 sm:mt-0 inline-flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <Plus size={20} />
          <span>Add Product</span>
        </Link>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2">
              <Filter size={20} className="text-gray-400" />
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Products</option>
                <option value="low-stock">Low Stock</option>
                <option value="high-price">High Value</option>
              </select>
            </div>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="newest">Newest First</option>
              <option value="name">Name A-Z</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="stock">Stock Level</option>
            </select>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {filteredProducts.length > 0 ? (
          <>
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Showing {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
                {collection && ` in ${collection.name}`}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm 
                ? `No products match "${searchTerm}"` 
                : collection 
                ? `No products in ${collection.name} yet.`
                : 'You haven\'t added any products yet.'
              }
            </p>
            <Link
              to={collectionId ? `/create-product/${collectionId}` : '/create-product'}
              className="inline-flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              <Plus size={20} />
              <span>Add Product</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products; 